import type { DashboardToday } from '@peles/shared';
import { apiClient } from './client';
import { monthRangeWindow } from '../utils/eventDates';

export interface DashboardStats {
  totalEvents: number;
  upcomingEvents: number;
  totalGuests: number;
  unpaidTotal: number;
  recentInquiries: number;
  revenueThisYear: number;
  totalProfit: number;
}

export interface UpcomingEvent {
  id: string;
  name: string;
  startAt: string;
  endAt: string | null;
  status: string;
  eventType: string | null;
  venue: string | null;
  /** Sum of maleCount+femaleCount+kidCount across all confirmed guests. */
  confirmedGuests: number;
  /** Sum of maleCount+femaleCount+kidCount across all guests. */
  totalGuests: number;
  /** People in parties that have not answered yet (rsvpStatus 'invited'). */
  pendingGuests: number;
  /** People in parties that answered maybe. */
  maybeGuests: number;
  /** People in parties that declined. */
  declinedGuests: number;
  /** Sum of expense item prices for this event. */
  totalExpenses: number;
  /** Cash the client still needs to provide (Option 1; same formula as client ledger). */
  balanceDue: number;
  /** Resolved presigned URL of the event-specific logo (null when none). */
  logoUrl: string | null;
}

export interface RecentInquiry {
  id: string;
  name: string;
  email: string;
  eventType: string | null;
  createdAt: string;
}

export interface OverduePayment {
  id: string;
  eventName: string;
  eventId: string;
  category: string | null;
  vendor: string | null;
  /** Total expense price (kept for back-compat). */
  amount: number;
  /** Sum of payments recorded against this expense. */
  paid: number;
  /** Outstanding balance (amount - paid). Always > 0 here, since fully-paid items are filtered out server-side. */
  balance: number;
  dueDate: string | null;
}

export interface UpcomingChecklistItem {
  id: string;
  title: string;
  status: string;
  isDone: boolean;
  dueDate: string | null;
  assigneeName: string | null;
  eventName: string | null;
  eventId: string | null;
  checklistTitle: string | null;
  sectionName: string | null;
}

export interface DashboardData {
  stats: DashboardStats;
  upcomingEvents: UpcomingEvent[];
  recentInquiries: RecentInquiry[];
  overduePayments: OverduePayment[];
  upcomingChecklistItems: UpcomingChecklistItem[];
}

/**
 * `upcoming` is the window the Upcoming (30d) tile counts — the events list's
 * This month range in the browser's own days, so the tile and the list it
 * opens agree.
 */
export async function fetchDashboardStats(
  upcoming: { start: string; end: string } = monthRangeWindow(),
): Promise<DashboardData> {
  const { data } = await apiClient.get<DashboardData>('/dashboard/stats', {
    params: { upcomingStart: upcoming.start, upcomingEnd: upcoming.end },
  });
  return data;
}

/** The browser's own day: its date, and the instants it and the week after it span locally. */
export interface LocalDay {
  date: string;
  start: string;
  end: string;
  /** Local midnight six days after `end` — not a fixed 144 hours when DST changes. */
  weekEnd: string;
}

export function localDay(now = new Date()): LocalDay {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${y}-${pad(m + 1)}-${pad(d)}`,
    start: new Date(y, m, d).toISOString(),
    end: new Date(y, m, d + 1).toISOString(),
    weekEnd: new Date(y, m, d + 7).toISOString(),
  };
}

export async function fetchDashboardToday(day: LocalDay = localDay()): Promise<DashboardToday> {
  const { data } = await apiClient.get<DashboardToday>('/dashboard/today', { params: day });
  return data;
}
